'use client'

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useDitherRepulsion } from '@/hooks/use-dither-repulsion'
import { supportsWebGL } from '@/lib/dither'

const HeroTextDitherThree = lazy(() =>
  import('./hero-text-dither-three').then((m) => ({ default: m.HeroTextDitherThree })),
)

type ToneMap = { width: number; height: number; rows: string[] }

/**
 * Renders the title text on a canvas at 2× resolution, samples the alpha
 * channel on a grid, and returns a dither tone map matching the text shape.
 *
 * The grid dimensions are derived from the title's CSS pixel size so that
 * dot positions (laid out as percentages of the container) map exactly to
 * the canvas pixels — no rounding drift.
 */
function generateTextToneMap(titleEl: HTMLElement, text: string): ToneMap {
  const style = getComputedStyle(titleEl)
  const rect = titleEl.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return { width: 0, height: 0, rows: [] }

  const scale = 2
  const targetCellCss = 3 // desired CSS pixels per grid cell (smaller = finer dots)

  // Grid dimensions in CSS-pixel-aligned cells (round, not ceil, so the
  // mapping containerWidth / gridWidth is as close to targetCellCss as possible).
  const gridWidth = Math.max(1, Math.round(rect.width / targetCellCss))
  const gridHeight = Math.max(1, Math.round(rect.height / targetCellCss))

  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(rect.width * scale)
  canvas.height = Math.ceil(rect.height * scale)
  const ctx = canvas.getContext('2d')!

  ctx.scale(scale, scale)
  ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  ctx.fillStyle = '#000'
  ctx.textBaseline = 'alphabetic'

  // Apply letter spacing if the browser supports it on canvas.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(ctx as any).letterSpacing = style.letterSpacing
  } catch {
    /* ignored — slight width difference is fine for dithering */
  }

  // Wrap text to match the title's rendered line breaks. Split on explicit
  // newlines first, then word-wrap each segment to fit the container width.
  const maxWidth = rect.width
  const lines: string[] = []
  for (const segment of text.split('\n')) {
    const words = segment.split(' ')
    let currentLine = ''
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word
      if (ctx.measureText(testLine).width <= maxWidth) {
        currentLine = testLine
      } else {
        if (currentLine) lines.push(currentLine)
        currentLine = word
      }
    }
    if (currentLine) lines.push(currentLine)
  }

  const fontSize = parseFloat(style.fontSize)
  const lineHeight = fontSize * 1.1 // matches line-height: 1.1
  // Position the baseline to match the DOM. fontBoundingBoxAscent (the font's
  // full ascent) sits slightly too low; actualBoundingBoxAscent (only the
  // rendered glyphs) sits slightly too high. The average matches the DOM.
  const metrics = ctx.measureText(lines[0] || text)
  const fontAscent = metrics.fontBoundingBoxAscent ?? fontSize * 0.82
  const glyphAscent = metrics.actualBoundingBoxAscent ?? fontSize * 0.75
  const ascent = (fontAscent + glyphAscent) / 2
  const baseline = ascent
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], 0, baseline + i * lineHeight)
  }

  // Sample alpha values into a hex grid. Cell sizes are computed from the
  // exact canvas and grid dimensions so that grid column x maps to CSS
  // pixel (x / gridWidth) * rect.width — matching the dot's percentage
  // position in the container perfectly.
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
  const cellSizeX = canvas.width / gridWidth
  const cellSizeY = canvas.height / gridHeight
  const rows = Array.from({ length: gridHeight }, (_, y) =>
    Array.from({ length: gridWidth }, (_, x) => {
      const px = Math.min(Math.round(x * cellSizeX), canvas.width - 1)
      const py = Math.min(Math.round(y * cellSizeY), canvas.height - 1)
      const alpha = data[(py * canvas.width + px) * 4 + 3]
      return Math.round((alpha / 255) * 15).toString(16)
    }).join(''),
  )

  return { width: gridWidth, height: gridHeight, rows }
}

export function HeroTextDither({ text }: { text: string }) {
  const ditherRef = useRef<HTMLDivElement>(null)
  const artRef = useRef<HTMLDivElement>(null)
  const [toneMap, setToneMap] = useState<ToneMap | null>(null)
  // null during SSR / before hydration; false falls back to DOM span dots.
  const [webgl, setWebgl] = useState<boolean | null>(() =>
    typeof document === 'undefined' ? null : supportsWebGL(),
  )
  useEffect(() => {
    // The measured <h1> is a sibling inside .home-hero-title-wrapper — find
    // it through the wrapper so no ref plumbing crosses the RSC boundary.
    const title = ditherRef.current?.parentElement?.querySelector<HTMLElement>(
      '.home-hero-title',
    )
    if (!title) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const generate = () => {
      if (cancelled) return
      const tone = generateTextToneMap(title, text)
      if (!cancelled && tone.width > 0) setToneMap(tone)
    }

    // Generate right after first paint so the flock-in overlaps the title's
    // own fade-in (home-fade-in 1.2s ease 0.1s). The tone-map raster is cheap
    // compared to the old DOM-dot build this delay was sized for, and the
    // renderer it feeds lives on the GPU anyway.
    // Regenerate once fonts have loaded (font swap may change text metrics).
    timer = setTimeout(() => {
      generate()
      if (document.fonts) document.fonts.ready.then(generate)
    }, 250)

    // Regenerate on resize (debounced) since the title uses clamp() font sizing.
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timer)
      timer = setTimeout(generate, 200)
    })
    resizeObserver.observe(title)

    return () => {
      cancelled = true
      clearTimeout(timer)
      resizeObserver.disconnect()
    }
  }, [text])

  const shapes = useMemo(
    () =>
      toneMap
        ? [
            {
              tone: toneMap,
              className: 'hero-text-dither-shape',
              fillContainer: true,
              dotScale: 0.5,
              hidden: true,
              entrance: true,
            },
          ]
        : [],
    [toneMap],
  )

  useDitherRepulsion(ditherRef, artRef, {
    shapes,
    pointerTargetSelector: '.home-hero',
    enabled: webgl === false,
  })

  return (
    <div ref={ditherRef} className="hero-text-dither" aria-hidden="true">
      <div ref={artRef} className="hero-text-dither-art" />
      {webgl && toneMap && (
        <Suspense fallback={null}>
          <HeroTextDitherThree
            rootRef={ditherRef}
            tone={toneMap}
            onUnavailable={() => setWebgl(false)}
          />
        </Suspense>
      )}
    </div>
  )
}
